import { MutableRefObject, useState, useEffect } from 'react';
import { useMutation, useLazyQuery, gql } from '@apollo/client';
import { Button, LabeledDiv } from 'components';
import { useModifiedTranslation } from 'hooks/useModifiedTranslation';
import { toast } from 'react-toastify';

const STORAGE_KEY = 'tradingAgreementFormData';

const IMPORT_CONTACTS_MUTATION = gql`
    mutation importContacts($contactList: [ContactDTOInput]) {
        importContacts(contactList: $contactList)
    }
`;

const GET_CONTACT_QUERY = gql`
    query GetContact($id: Long!) {
        contact(id: $id) {
            memo
        }
    }
`;

interface ContactInput {
    contactId: string;
    memo: string;
}

interface TradingAgreementFormProps {
    onClose: () => void;
    onComplete: () => void;
    modalInitialFocusRef: MutableRefObject<null>;
    contactId?: string;
}

interface FormData {
    // Investment types
    assetManagement: boolean;
    independentInvestments: boolean;
    stocks: boolean;
    bonds: boolean;
    funds: boolean;
    etf: boolean;
    moneyMarket: boolean;
    derivatives: boolean;
    structured: boolean;
    other: boolean;
    
    // Profile attributes
    frequency: string;
    risk: string;
    years: number;
    totalAssets: number;
    averageInvestment: number;
    noTransactions: number;
    horizon: number;
}

export const TradingAgreementForm = ({ onClose, onComplete, modalInitialFocusRef, contactId }: TradingAgreementFormProps) => {
    const [getContact, { data: contactData, loading: contactLoading }] = useLazyQuery(GET_CONTACT_QUERY);
    const [importContacts, { loading: submitting }] = useMutation(IMPORT_CONTACTS_MUTATION);
    const { t } = useModifiedTranslation();
    const [formData, setFormData] = useState<FormData>({
        // Investment types
        assetManagement: false,
        independentInvestments: false,
        stocks: false,
        bonds: false,
        funds: false,
        etf: false,
        moneyMarket: false,
        derivatives: false,
        structured: false,
        other: false,
        
        // Profile attributes
        frequency: 'Daily',
        risk: 'Medium',
        years: 0,
        totalAssets: 0,
        averageInvestment: 0,
        noTransactions: 0,
        horizon: 0,
    });

    const handleSubmit = async () => {
        // MOCK: Save to localStorage only (no server call)
        try {
            const keyFiguresData = [
                `assetMgmt:${formData.assetManagement}`,
                `independent:${formData.independentInvestments}`,
                `stocks:${formData.stocks}`,
                `bonds:${formData.bonds}`,
                `funds:${formData.funds}`,
                `etf:${formData.etf}`,
                `moneyMkt:${formData.moneyMarket}`,
                `derivatives:${formData.derivatives}`,
                `structured:${formData.structured}`,
                `other:${formData.other}`,
                `freq:${formData.frequency}`,
                `risk:${formData.risk}`,
                `years:${formData.years || 0}`,
                `assets:${formData.totalAssets || 0}`,
                `avgInv:${formData.averageInvestment || 0}`,
                `txns:${formData.noTransactions || 0}`,
                `horizon:${formData.horizon}`,
            ].join(',');

            // Save form data to localStorage
            localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
            localStorage.setItem('tradingAgreementCompleted', 'true');
            localStorage.setItem('tradingAgreementDate', new Date().toISOString());
            localStorage.setItem('tradingAgreementKeyFigures', keyFiguresData);

            console.log('MOCK: Saved trading agreement to localStorage:', {
                contactId: contactId || '1000004916',
                memo: keyFiguresData,
                formData,
            });

            // Simulate API delay
            await new Promise(resolve => setTimeout(resolve, 500));

            toast.success(t('tradingAgreement.submittedSuccessfully') + ' (Mock)');
            onComplete();
            onClose();
        } catch (error: unknown) {
            console.error('Failed to save trading agreement:', error);
            const message = error instanceof Error ? error.message : undefined;
            toast.error(message || t('tradingAgreement.submissionFailed'));
        }
    };

    const isValid = formData.risk && formData.horizon > 0;

    const handleTestFetch = async () => {
        try {
            const testContactId = contactId || '151';
            console.log('Fetching contact:', testContactId);
            
            const result = await getContact({
                variables: { id: parseInt(testContactId) },
            });
            
            console.log('Contact data:', result.data);
            toast.success(`Contact memo: ${result.data?.contact?.memo || 'No memo found'}`);
        } catch (error: unknown) {
            console.error('Failed to fetch contact:', error);
            
            const message = error instanceof Error ? error.message : undefined;
            toast.error(message || 'Failed to fetch contact');
        }
    };

    return (
        <div className='max-w-6xl min-w-[min(90vw,_900px)]'>
            <div className='mb-4 text-sm text-gray-700'>
                <h2 className='mb-2 text-lg font-semibold'>{t('tradingAgreement.investmentProfile')}</h2>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Left Column */}
                <div className='space-y-4'>
                    {/* Experience Panel */}
                    <div className='rounded-lg border'>
                        <div className='py-3 px-4 bg-gray-50 border-b'>
                            <h3 className='text-sm font-semibold'>{t('tradingAgreement.experience')}</h3>
                        </div>
                        <div className='p-4 space-y-3'>
                            <label className='flex gap-2 items-start cursor-pointer'>
                                <input
                                    ref={modalInitialFocusRef}
                                    type='checkbox'
                                    checked={formData.assetManagement}
                                    onChange={e => setFormData({ ...formData, assetManagement: e.target.checked })}
                                    className='mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500'
                                />
                                <span className='text-sm'>{t('tradingAgreement.assetManagement')}</span>
                            </label>
                            <label className='flex gap-2 items-start cursor-pointer'>
                                <input
                                    type='checkbox'
                                    checked={formData.independentInvestments}
                                    onChange={e => setFormData({ ...formData, independentInvestments: e.target.checked })}
                                    className='mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500'
                                />
                                <span className='text-sm'>{t('tradingAgreement.independentInvestments')}</span>
                            </label>
                            <LabeledDiv label={t('tradingAgreement.years')} className='mt-3'>
                                <input
                                    type='number'
                                    min='0'
                                    value={formData.years || ''}
                                    onChange={e => setFormData({ ...formData, years: parseInt(e.target.value) || 0 })}
                                    className='p-2 w-full text-sm rounded border focus:ring-2 focus:ring-blue-500 focus:outline-none'
                                />
                            </LabeledDiv>
                        </div>
                    </div>

                    {/* Experience of Investment Products Panel */}
                    <div className='rounded-lg border'>
                        <div className='py-3 px-4 bg-gray-50 border-b'>
                            <h3 className='text-sm font-semibold'>{t('tradingAgreement.experienceInInstruments')}</h3>
                        </div>
                        <div className='p-4'>
                            <div className='grid grid-cols-2 gap-x-4 gap-y-3'>
                                <label className='flex gap-2 items-start cursor-pointer'>
                                    <input
                                        type='checkbox'
                                        checked={formData.stocks}
                                        onChange={e => setFormData({ ...formData, stocks: e.target.checked })}
                                        className='mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500'
                                    />
                                    <span className='text-sm'>{t('tradingAgreement.stocks')}</span>
                                </label>
                                <label className='flex gap-2 items-start cursor-pointer'>
                                    <input
                                        type='checkbox'
                                        checked={formData.moneyMarket}
                                        onChange={e => setFormData({ ...formData, moneyMarket: e.target.checked })}
                                        className='mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500'
                                    />
                                    <span className='text-sm'>{t('tradingAgreement.moneyMarket')}</span>
                                </label>
                                <label className='flex gap-2 items-start cursor-pointer'>
                                    <input
                                        type='checkbox'
                                        checked={formData.bonds}
                                        onChange={e => setFormData({ ...formData, bonds: e.target.checked })}
                                        className='mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500'
                                    />
                                    <span className='text-sm'>{t('tradingAgreement.bonds')}</span>
                                </label>
                                <label className='flex gap-2 items-start cursor-pointer'>
                                    <input
                                        type='checkbox'
                                        checked={formData.derivatives}
                                        onChange={e => setFormData({ ...formData, derivatives: e.target.checked })}
                                        className='mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500'
                                    />
                                    <span className='text-sm'>{t('tradingAgreement.derivatives')}</span>
                                </label>
                                <label className='flex gap-2 items-start cursor-pointer'>
                                    <input
                                        type='checkbox'
                                        checked={formData.funds}
                                        onChange={e => setFormData({ ...formData, funds: e.target.checked })}
                                        className='mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500'
                                    />
                                    <span className='text-sm'>{t('tradingAgreement.funds')}</span>
                                </label>
                                <label className='flex gap-2 items-start cursor-pointer'>
                                    <input
                                        type='checkbox'
                                        checked={formData.structured}
                                        onChange={e => setFormData({ ...formData, structured: e.target.checked })}
                                        className='mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500'
                                    />
                                    <span className='text-sm'>{t('tradingAgreement.structured')}</span>
                                </label>
                                <label className='flex gap-2 items-start cursor-pointer'>
                                    <input
                                        type='checkbox'
                                        checked={formData.etf}
                                        onChange={e => setFormData({ ...formData, etf: e.target.checked })}
                                        className='mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500'
                                    />
                                    <span className='text-sm'>{t('tradingAgreement.etf')}</span>
                                </label>
                                <label className='flex gap-2 items-start cursor-pointer'>
                                    <input
                                        type='checkbox'
                                        checked={formData.other}
                                        onChange={e => setFormData({ ...formData, other: e.target.checked })}
                                        className='mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500'
                                    />
                                    <span className='text-sm'>{t('tradingAgreement.other')}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className='space-y-4'>
                    {/* Investment Activity Panel */}
                    <div className='rounded-lg border'>
                        <div className='py-3 px-4 bg-gray-50 border-b'>
                            <h3 className='text-sm font-semibold'>{t('tradingAgreement.investmentActivity')}</h3>
                        </div>
                        <div className='p-4 space-y-4'>
                            <div className='grid grid-cols-2 gap-4'>
                                <LabeledDiv label={t('tradingAgreement.totalAssets')}>
                                    <input
                                        type='number'
                                        min='0'
                                        value={formData.totalAssets || ''}
                                        onChange={e => setFormData({ ...formData, totalAssets: parseFloat(e.target.value) || 0 })}
                                        className='p-2 w-full text-sm rounded border focus:ring-2 focus:ring-blue-500 focus:outline-none'
                                        placeholder='€'
                                    />
                                </LabeledDiv>
                                <LabeledDiv label={t('tradingAgreement.frequency')}>
                                    <select
                                        value={formData.frequency}
                                        onChange={e => setFormData({ ...formData, frequency: e.target.value })}
                                        className='p-2 w-full text-sm rounded border focus:ring-2 focus:ring-blue-500 focus:outline-none'
                                    >
                                        <option value='Daily'>{t('tradingAgreement.daily')}</option>
                                        <option value='Weekly'>{t('tradingAgreement.weekly')}</option>
                                        <option value='Monthly'>{t('tradingAgreement.monthly')}</option>
                                        <option value='Yearly'>{t('tradingAgreement.yearly')}</option>
                                        <option value='Less'>{t('tradingAgreement.less')}</option>
                                    </select>
                                </LabeledDiv>
                            </div>
                <div className='grid grid-cols-2 gap-4'>
                    <LabeledDiv label={t('tradingAgreement.averageInvestment')}>
                        <input
                            type='number'
                            min='0'
                            value={formData.averageInvestment || ''}
                            onChange={e => setFormData({ ...formData, averageInvestment: parseFloat(e.target.value) || 0 })}
                            className='p-2 w-full text-sm rounded border focus:ring-2 focus:ring-blue-500 focus:outline-none'
                            placeholder='€'
                        />
                    </LabeledDiv>
                                <LabeledDiv label={t('tradingAgreement.noTransactions')}>
                                    <input
                                        type='number'
                                        min='0'
                                        value={formData.noTransactions || ''}
                                        onChange={e => setFormData({ ...formData, noTransactions: parseInt(e.target.value) || 0 })}
                                        className='p-2 w-full text-sm rounded border focus:ring-2 focus:ring-blue-500 focus:outline-none'
                                    />
                                </LabeledDiv>
                            </div>
                        </div>
                    </div>

                    {/* Risk and Investment Horizon Panel */}
                    <div className='rounded-lg border'>
                        <div className='py-3 px-4 bg-gray-50 border-b'>
                            <h3 className='text-sm font-semibold'>{t('tradingAgreement.riskAndHorizon')}</h3>
                        </div>
                        <div className='p-4'>
                            <div className='grid grid-cols-2 gap-4'>
                                <LabeledDiv label={`${t('tradingAgreement.risk')} *`}>
                                    <select
                                        value={formData.risk}
                                        onChange={e => setFormData({ ...formData, risk: e.target.value })}
                                        className='p-2 w-full text-sm rounded border focus:ring-2 focus:ring-blue-500 focus:outline-none'
                                        required
                                    >
                                        <option value=''>Select...</option>
                                        <option value='High'>{t('tradingAgreement.high')}</option>
                                        <option value='Medium'>{t('tradingAgreement.medium')}</option>
                                        <option value='Low'>{t('tradingAgreement.low')}</option>
                                    </select>
                                </LabeledDiv>
                                <LabeledDiv label={`${t('tradingAgreement.horizon')} *`}>
                                    <input
                                        type='number'
                                        min='1'
                                        value={formData.horizon || ''}
                                        onChange={e => setFormData({ ...formData, horizon: parseInt(e.target.value) || 0 })}
                                        className='p-2 w-full text-sm rounded border focus:ring-2 focus:ring-blue-500 focus:outline-none'
                                        placeholder='Years'
                                        required
                                    />
                                </LabeledDiv>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='flex gap-2 justify-end pt-6 mt-6 border-t'>
                <Button variant='Secondary' onClick={handleTestFetch} disabled={contactLoading}>
                    {contactLoading ? 'Loading...' : 'Test Fetch Contact'}
                </Button>
                <Button variant='Secondary' onClick={onClose}>
                    {t('common.cancel')}
                </Button>
                <Button onClick={handleSubmit} disabled={!isValid}>
                    {t('tradingAgreement.submit')} (Mock)
                </Button>
            </div>
        </div>
    );
}

